/**
 * API Client for SurabayaTicket Backend
 * Base HTTP client with authentication support
 */

export const BASE_URL =
  import.meta.env.VITE_API_URL || "https://api.gizrabbitland.com";
export const API_BASE_URL = `${BASE_URL}/v1`;

interface RequestOptions {
  headers?: Record<string, string>;
  body?: unknown;
}

class ApiError extends Error {
  constructor(public status: number, message: string, public data?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Get stored authentication token
 */
export function getAuthToken(): string | null {
  return localStorage.getItem("auth_token");
}

/**
 * Set authentication token
 */
export function setAuthToken(token: string): void {
  localStorage.setItem("auth_token", token);
}

/**
 * Clear authentication token
 */
export function clearAuthToken(): void {
  localStorage.removeItem("auth_token");
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/**
 * Build headers with optional authentication
 */
function buildHeaders(
  customHeaders?: Record<string, string>,
  isFormData: boolean = false
): Headers {
  const headers = new Headers({
    Accept: "application/json",
    ...customHeaders,
  });

  if (!isFormData) {
    headers.set("Content-Type", "application/json");
  }

  const token = getAuthToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return headers;
}

/**
 * Handle API response
 */
async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type");
  const isJson = contentType?.includes("application/json");

  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    if (response.status === 401) {
      clearAuthToken();
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }

    throw new ApiError(
      response.status,
      data?.message || `HTTP error ${response.status}`,
      data
    );
  }

  return data as T;
}

/**
 * GET request
 */
export async function get<T>(
  endpoint: string,
  params?: Record<string, any>,
  options?: RequestOptions,
  isAuth: boolean = true
): Promise<T> {
  let url = `${API_BASE_URL}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  if (isAuth) {
    const token = getAuthToken();
    if (!token) {
      throw new ApiError(401, "Unauthorized");
    }
  }

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(options?.headers),
  });

  return handleResponse<T>(response);
}

/**
 * POST request
 */
export async function post<T>(
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<T> {
  const isFormData = body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "POST",
    headers: buildHeaders(options?.headers, isFormData),
    body: isFormData
      ? (body as FormData)
      : body
        ? JSON.stringify(body)
        : undefined,
  });

  return handleResponse<T>(response);
}

/**
 * PATCH request
 */
export async function patch<T>(
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<T> {
  const isFormData = body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "PATCH",
    headers: buildHeaders(options?.headers, isFormData),
    body: isFormData
      ? (body as FormData)
      : body
        ? JSON.stringify(body)
        : undefined,
  });

  return handleResponse<T>(response);
}

/**
 * PUT request
 */
export async function put<T>(
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<T> {
  const isFormData = body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "PUT",
    headers: buildHeaders(options?.headers, isFormData),
    body: isFormData
      ? (body as FormData)
      : body
        ? JSON.stringify(body)
        : undefined,
  });

  return handleResponse<T>(response);
}

/**
 * DELETE request
 */
export async function del<T>(
  endpoint: string,
  options?: RequestOptions
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "DELETE",
    headers: buildHeaders(options?.headers),
  });

  return handleResponse<T>(response);
}

export { ApiError };
export default { get, post, patch, put, del };
